"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete02Icon, Edit02Icon, FloppyDiskIcon, PercentIcon } from "@hugeicons/core-free-icons";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post } from "../utils/service";
import Drawer from "@/components/ui/Drawer";

interface LoanPlan {
  id: string;
  name: string;
  duration_days: number;
  interest_rate: string;
  overdue_penalty_rate: string | null;
  overdue_penalty_period: string | null;
  is_active: boolean;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} position="bottom" heightPercentage={80} showCloseButton={false}>
        <div className="h-full flex flex-col bg-black text-white">{children}</div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

interface PlanFormState {
  name: string;
  duration_days: string;
  interest_rate: string;
  overdue_penalty_rate: string;
  overdue_penalty_period: string;
  is_active: boolean;
  notes: string;
}

const emptyForm: PlanFormState = {
  name: "",
  duration_days: "30",
  interest_rate: "20",
  overdue_penalty_rate: "",
  overdue_penalty_period: "daily",
  is_active: true,
  notes: "",
};

const PlanForm = ({
  isOpen,
  onClose,
  onSuccess,
  plan,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  plan: LoanPlan | null;
}) => {
  const [formData, setFormData] = useState<PlanFormState>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEdit = !!plan;

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        duration_days: String(plan.duration_days),
        interest_rate: String(plan.interest_rate),
        overdue_penalty_rate: plan.overdue_penalty_rate ? String(plan.overdue_penalty_rate) : "",
        overdue_penalty_period: plan.overdue_penalty_period || "daily",
        is_active: plan.is_active,
        notes: plan.notes || "",
      });
    } else if (isOpen) {
      setFormData(emptyForm);
    }
  }, [plan, isOpen]);

  const handleChange = (field: keyof PlanFormState, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        duration_days: parseInt(formData.duration_days, 10),
        interest_rate: parseFloat(formData.interest_rate),
        overdue_penalty_rate: formData.overdue_penalty_rate ? parseFloat(formData.overdue_penalty_rate) : null,
        overdue_penalty_period: formData.overdue_penalty_period,
        is_active: formData.is_active,
        notes: formData.notes || null,
      };
      const response = isEdit
        ? await put(`/loan-plans/${plan!.id}`, payload)
        : await post("/loan-plans", payload);
      if ((response as { success: boolean }).success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error saving loan plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">{isEdit ? "Edit Loan Plan" : "Add Loan Plan"}</h2>
          <p className="text-xs text-gray-400 mt-1">Define standard terms borrowers can be assigned to</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Plan Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Duration (days) *</label>
            <input
              type="number"
              min={1}
              required
              value={formData.duration_days}
              onChange={(e) => handleChange("duration_days", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Interest Rate (%) *</label>
            <input
              type="number"
              step="0.01"
              min={0}
              required
              value={formData.interest_rate}
              onChange={(e) => handleChange("interest_rate", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Overdue Penalty Rate (%)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={formData.overdue_penalty_rate}
              onChange={(e) => handleChange("overdue_penalty_rate", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Penalty Period</label>
            <select
              value={formData.overdue_penalty_period}
              onChange={(e) => handleChange("overdue_penalty_period", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => handleChange("is_active", e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-600 text-secondary focus:ring-2 focus:ring-secondary"
          />
          <span className="text-xs text-gray-300">Active (selectable when creating new loans)</span>
        </label>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.name || !formData.duration_days || !formData.interest_rate}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-primary text-xs font-semibold rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <HugeiconsIcon icon={FloppyDiskIcon} size={16} />
              {isEdit ? "Update Plan" : "Create Plan"}
            </>
          )}
        </button>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

function ActionsCell({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <HugeiconsIcon icon={Edit02Icon} size={12} />
            Edit Plan
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <HugeiconsIcon icon={Delete02Icon} size={12} />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function LoanPlansPage() {
  const [plans, setPlans] = useState<LoanPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<LoanPlan | null>(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get<{ success: boolean; data: { loan_plans: LoanPlan[] } }>("/loan-plans");
      if (response.success) {
        setPlans(response.data.loan_plans || []);
      }
    } catch (error) {
      console.error("Error fetching loan plans:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleDelete = async (id: string) => {
    if (!await confirmDialog("Delete this loan plan?")) return;
    try {
      await del(`/loan-plans/${id}`);
      setPlans((prev) => prev.filter((p) => p.id !== id));
      notify("Loan plan deleted successfully");
    } catch (error) {
      console.error("Error deleting loan plan:", error);
      notify("Error deleting loan plan");
    }
  };

  const handleBatchDelete = async (rows: LoanPlan[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected loan plan${rows.length === 1 ? "" : "s"}?`)) return;
    const results = await Promise.allSettled(rows.map((r) => del(`/loan-plans/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setPlans((prev) => prev.filter((p) => !deleted.has(p.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} loan plan${deleted.size === 1 ? "" : "s"} deleted successfully`);
  };

  const columns: ColumnDef<LoanPlan>[] = [
    {
      accessorKey: "name",
      header: "Plan",
      cell: ({ row }) => <p className="text-xs font-semibold text-gray-900">{row.original.name}</p>,
    },
    {
      accessorKey: "duration_days",
      header: "Duration",
      cell: ({ row }) => <p className="text-xs text-gray-700">{row.original.duration_days} days</p>,
    },
    {
      accessorKey: "interest_rate",
      header: "Interest Rate",
      cell: ({ row }) => <p className="text-xs text-gray-700">{row.original.interest_rate}%</p>,
    },
    {
      id: "penalty",
      header: "Overdue Penalty",
      cell: ({ row }) => (
        <p className="text-xs text-gray-700">
          {row.original.overdue_penalty_rate
            ? `${row.original.overdue_penalty_rate}% / ${row.original.overdue_penalty_period}`
            : "—"}
        </p>
      ),
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.original.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
          {row.original.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          onEdit={() => {
            setEditingPlan(row.original);
            setShowForm(true);
          }}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Loan Plans" description="Standard terms borrowers can be assigned to">
          <button
            onClick={() => {
              setEditingPlan(null);
              setShowForm(true);
            }}
            className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200 min-w-[140px]"
          >
            Add Plan
          </button>
        </PageHeader>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden has-[[data-card-view]]:border-0 has-[[data-card-view]]:bg-transparent has-[[data-card-view]]:shadow-none">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider
                columns={columns}
                data={plans}
                onAddRow={() => {
                  setEditingPlan(null);
                  setShowForm(true);
                }}
                addRowLabel="New loan plan"
                onDeleteSelected={handleBatchDelete}
              >
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {plans.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <HugeiconsIcon icon={PercentIcon} size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No loan plans yet</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    Create a standard set of terms (duration, interest rate) to speed up loan creation.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPlan(null);
                      setShowForm(true);
                    }}
                    className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                  >
                    Add First Plan
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PlanForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={() => {
          fetchPlans();
          notify(editingPlan ? "Loan plan updated successfully" : "Loan plan created successfully");
        }}
        plan={editingPlan}
      />
    </div>
  );
}

export default LoanPlansPage;
